import ComponentBase, { ComponentBaseProps } from "../../../components/ComponentBase";
import RupIcons from "../../../RupIcons";
import { 
	SearchBlockType, 
	VocabColorPalette, 
	ContentSearchBlock, 
	SearchBlockOpType, 
	KeyCode
} from "../typings";
import DropdownOperator from "./DropdownOperator";
import Utils from "./common/Utils";
import Tooltip from "./Tooltip";

export interface SearchBlockProps extends ComponentBaseProps {
	readonly vocabColorPalette?: VocabColorPalette;
	readonly onClear?: (block: SearchBlock) => void;
	readonly onKeyDown?: (event: JQueryKeyEventObject) => void;
	readonly renderInput?: () => JQuery;
	readonly addTextBlockIfNecessary?: () => void;
	readonly term?: ContentSearchBlock;
	readonly isPresentation?: boolean;
	readonly onPlaceholderLostFocus?: (inputElement: JQuery) => void;
	readonly operatorUpdatedHandler?: () => void;
}

const operatorClass = "rup-global-search__chip rup-global-search__chip--operator";
export default class SearchBlock extends ComponentBase<SearchBlockProps>{

	private buttonClear: JQuery;
	private inputContainer: JQuery;
	private hiddenBlocks: JQuery[] = [];

	public placeholder: JQuery;
	public placeholderInput: JQuery;
	public input: JQuery;

	public isEditMode = false;

	private term: ContentSearchBlock;
	private vocabColorPalette: VocabColorPalette;

	constructor(props: SearchBlockProps) {
		super(props);
		this.term = this.props.term;
		this.vocabColorPalette = props.vocabColorPalette;
	}

	public get blockTerm(): ContentSearchBlock {
		return this.term;
	}

	public get text(): string {
		return this.term?.text;
	}

	public get_text(): string {
		return this.text;
	}

	public get vocabularyId(): number {
		return this.term?.descriptor?.vocabularyId;
	}

	public get descriptorId(): number {
		return this.term?.descriptor?.descriptorId;
	}

	public get type(): SearchBlockType {
		return this.term?.type;
	}

	public get opType(): SearchBlockOpType {
		return this.term?.opType || SearchBlockOpType[this.text];
	}

	private get isVocabulary(): boolean {
		return this.term?.type !== SearchBlockType.FreeText && this.term?.type !== SearchBlockType.Operator;
	}

	public get isOperator(): boolean {
		return this.term?.type === SearchBlockType.Operator;
	}

	public get isSpecialOperator(): boolean {
		return this.term?.opType === SearchBlockOpType.LeftParens || this.term?.opType === SearchBlockOpType.RightParens;
	}

	public get isBoolOperator(): boolean {
		return (this.term?.type === SearchBlockType.Operator) &&
			!(this.term?.opType === SearchBlockOpType.LeftParens || this.term?.opType === SearchBlockOpType.RightParens);
	}

	public get isOpeningBracket(): boolean {
		return this.term?.opType === SearchBlockOpType.LeftParens;
	}

	public get isClosingBracket(): boolean {
		return this.term?.opType === SearchBlockOpType.RightParens;
	}

	private get isLastAndSingleBlock(): boolean {
		const nextBlock = $(this.container).nextAll("div:first");
		if (!nextBlock) {
			return false;
		}
		const prevBlock = $(this.container).prevAll("div.rup-global-search__chip:first");
		if (prevBlock?.length) {
			return false;
		}
		return nextBlock.hasClass("rup-global-search__chip-insertion") &&
			nextBlock.hasClass("globalSearch__chip--mainInput");
	}

	private get mainInputBlock(): JQuery {
		return $(this.container).nextAll('div.globalSearch__chip--mainInput:first');
	}

	public getTooltipInfo(): JQuery {
		const content = $("<div>")
			.addClass("tooltip-container");

		if (this.term.invalid) {
			content.append($("<div>")
				.addClass("rup-caption-strong")
				.text(GlobalSearchLabels.ThisTermIsDeprecated));
				
			return content;
		}

		let info = null;
		switch (this.term.type) {
			case SearchBlockType.Vocab:
				info = { header: this.term.text, content: GlobalSearchLabels.CompleteVocabulary };
				break;
			case SearchBlockType.Descriptor:
			case SearchBlockType.Synonym:
				info = { bag: {} };
				info.bag[GlobalSearchLabels.Synonym] = this.term.synName || this.term.text;
				info.bag[GlobalSearchLabels.Vocabulary] = this.term.vocab;
				break;
			case SearchBlockType.FreeText:
				info = { header: this.term.text, content: GlobalSearchLabels.TextSearch };
				break;
		}

		if (info.header) {
			content.append($("<div>")
				.addClass("rup-caption-strong")
				.text(info.header));
		}

		if (info.content) {
			content.append($("<div>")
				.addClass("rup-caption")
				.append(info.content));
		}

		if (info.bag) {
			for (const i in info.bag) {
				content.append($("<div>")
					.append($("<span>")
						.addClass("rup-caption")
						.append(`${i}: `))
					.append($("<span>")
						.addClass("rup-caption-strong")
						.append(info.bag[i])));
			}
		}

		return content;
	}

	public getTooltipInfoExtended(): [JQuery, JQuery] {
		throw new Error("Not implemented");
	}

	protected renderInternal(): JQuery {
		if (!this.isOperator) {
			this.renderChipClear();
			const tooltip = new Tooltip(this);
			return (this.isVocabulary ? this.renderVocabBlock() : this.renderTextBlock())
				.attr("data-test-id", "search-chip")
				.on("mouseover", event =>
					tooltip.toggleVisibility(event))
				.on("mouseout", event =>  
					tooltip.toggleVisibility(event));

		}
		return this.props.isPresentation ? this.renderChipOperatorPresentation() : this.renderChipOperator();
			
	}

	public addChipPlaceholder(): void {
		this.placeholderInput = $("<input>")
			.addClass("rup-input-placeholder globalSearch__placeholder")
			.attr({
				type: "text"
			})
			.keydown((event) => {
				if (event.key !== KeyCode.TAB) {
					event.preventDefault();
				}
				this.props.onKeyDown(event);
			})
			.blur((event) => this.leavePlaceholderInput(event));

		this.placeholder = $("<div>")
			.addClass("rup-global-search__chip-placeholder globalSearch__chip--placeholder")
			.append(this.placeholderInput)
			.insertBefore(this.container);
	}

	private leavePlaceholderInput(event?: JQueryKeyEventObject & JQueryInputEventObject): void {
		if (!this.props.onPlaceholderLostFocus || !event) {
			return;
		}

		this.props.onPlaceholderLostFocus($(event.currentTarget));
	}

	private updateInputWidth(input: HTMLInputElement): void {
		if (!input) {
			return;
		}

		const newSize = (input?.value?.length || 0) + 1;
		input.setAttribute("size", Math.max(2, newSize).toString());
	}

	private renderInput(text = ""): JQuery {
		this.inputContainer = $("<div>")
			.addClass("rup-global-search__chip--insertion globalSearch__chip--insertion")
			.insertBefore(this.container);

		this.input = this.props.renderInput()
			.addClass("globalSearch__input")
			.val(text)
			.attr({
				"placeholder": "",
				"size": text.length
			})
			.css({ width: "100%" })
			.on("input", (event) => this.updateInputWidth(event.currentTarget as HTMLInputElement));

		$(".rup-global-search__chip").not(this.container)
			.on("click", e => this.editModeOff(e));

		$(document).not(".rup-global-search__chip")
			.on("click", e => {
				if (Utils.isGlobalSearchDialogEvent(e)) {
					return;
				}
				this.editModeOff(e);
			});
		this.inputContainer.append(this.input);

		return this.input;
	}

	public resetChanges(event?: JQueryEventObject): boolean {
		if (!this.isEditMode) {
			return false;
		}

		this.input.val(this.term.text);

		this.editModeOff(event);
		return true;
	}

	public editModeOff(event?: JQueryEventObject): void {
		if (!this.isEditMode) {
			return;
		}

		const text = this.input.val();
		const termText = this.term.text;

		this.hiddenBlocks?.forEach((b) => b?.show());
		this.hiddenBlocks = [];

		if (!text) {
			if (event) {
				if (event.preventDefault) {
					event.preventDefault();
				}
				if (event.stopPropagation) {
					event.stopPropagation();
				}
			}
			this.remove();
			return;
		} else if (event && text !== termText) {
			this.props.addTextBlockIfNecessary();
		}

		this.isEditMode = false;

		this.input.triggerHandler("input");
		this.input.remove();
		this.input = null;

		this.inputContainer?.remove();
		this.inputContainer = null;
	}

	protected renderVocabBlock(): JQuery {
		const text = $("<span>").attr("data-test-id", "search-chip-text").text(this.term.text);
		const colorPalette = this.vocabColorPalette;

		return $("<div>")
			.addClass("rup-global-search__chip globalSearch__chip")
			.toggleClass("rup-global-search__chip--blue", !colorPalette)
			.css("background-color", colorPalette?.Highlight)
			.hover(
				function () { $(this).css("background-color", colorPalette?.Hover); },
				function () { $(this).css("background-color", colorPalette?.Highlight); }
			)
			.append(
				$("<span>")
					.addClass("text-container")
					.append(text)
					.append(
						this.term.invalid ?
							$("<span>")
								.addClass("globalSearch__chip--warning rup-icon-btn rup-icon-btn--xs")
								.append(RupIcons.warning)
							: ""
					)
					.append(this.props.isPresentation ? "" : this.buttonClear)
					.css("color", colorPalette?.SelectedBar))
			.append(
				$("<span>")
					.attr("data-test-id", "search-chip-type")
					.text(this.term.vocab))
			.click(this.editModeOn.bind(this));
	}
	
	private toggleInputStyle(asMainInput = false): void {
		if (!this.input) {
			return;
		}

		if (asMainInput) {
			this.inputContainer
				.removeClass("globalSearch__chip--insertion")
				.addClass("globalSearch__chip--mainInput");
			this.input.removeClass("globalSearch__input");
			this.inputContainer.css({ "width": "98%" });
		} else {
			this.inputContainer
				.addClass("globalSearch__chip--insertion")
				.removeClass("globalSearch__chip--mainInput");
			this.input.addClass("globalSearch__input");
		}
	}

	private editModeOn(): void {
		if (this.props.isPresentation) {
			return;
		}

		this.isEditMode = true;
		this.renderInput(this.text);
		this.hiddenBlocks.push(this.container);
		this.hiddenBlocks.push(this.mainInputBlock);

		if (this.isLastAndSingleBlock) {
			this.toggleInputStyle(true);
		}
		this.hiddenBlocks?.forEach((b) => b?.hide());
		this.input.focus();
		this.input.triggerHandler("input");
	}

	public replaceBlock(term: ContentSearchBlock, vocabColorPalette: VocabColorPalette): void {
		this.editModeOff();
		this.vocabColorPalette = vocabColorPalette;
		this.term = term;

		const newBlock = this.renderInternal();
		this.containerInternal.replaceWith(newBlock);
		this.containerInternal = newBlock;
	}

	protected renderTextBlock(): JQuery {
		const text = $("<span>").attr("data-test-id", "search-chip-text").text(this.term.text);
		return $("<div>")
			.addClass("rup-global-search__chip globalSearch__chip globalSearch__chip--text")
			.append(
				$("<span>")
					.append(text)
					.append(this.props.isPresentation ? "" : this.buttonClear))
			.append(
				$("<span>")
					.attr("data-test-id", "search-chip-type")
					.text(GlobalSearchLabels.TextSearch))
			.click(() => {
				this.editModeOn();
			});
	}

	private renderChipClear(): JQuery {
		return this.buttonClear = $("<button>")
			.addClass("rup-icon-btn rup-icon-btn--xs rup-icon-btn--neutral")
			.append(RupIcons.clear)
			.attr({
				type: "button",
				title: "Remove Chip",
				"data-test-id": "search-chip-remove-btn"
			})
			.click((e) => {
				e.preventDefault();
				e.stopPropagation();
				this.remove();
			});
	}

	public clear(): void {
		this.animateFadeOut(() => {
			this.placeholder?.remove();
			this.input?.remove();
			this.inputContainer?.remove();
			this.container?.remove();
		});
	}

	private remove(): void {
		this.props.onClear(this);
	}

	private renderChipOperator(): JQuery {
		if (this.isSpecialOperator) {
			return this.renderSpecialOperator();
		}

		return new DropdownOperator(this.text, this.changeOperator.bind(this)).render();
	}

	private renderSpecialOperator(): JQuery {
		return $("<div>")
			.addClass(operatorClass)
			.attr("data-test-id", "search-operator")
			.append(
				$("<button>")
					.addClass("rup-global-search__dropdown-toggle globalSearch__button-toggle")
					.css("padding-right", "0.25rem")
					.append(this.text)
					.attr("disabled", "true")
			);
	}

	private renderSpecialOperatorPresentation(): JQuery {
		return $("<span>")
			.addClass(operatorClass)
			.attr("data-test-id", "search-operator")
			.append(
				$("<span>")
					.addClass("rup-global-search__dropdown-toggle")
					.text(this.text)
			);
	}

	private renderChipOperatorPresentation(): JQuery {
		return this.isSpecialOperator ?
			this.renderSpecialOperatorPresentation() :
			$("<span>")
				.addClass(operatorClass)
				.attr("data-test-id", "search-operator")
				.append(
					$("<span>")
						.addClass("rup-global-search__dropdown-toggle")
						.text(Utils.translatedOperators[this.text])
				);
	}

	private changeOperator(text: string): void {
		this.blockTerm.opType = SearchBlockOpType[text];
		this.blockTerm.text = text;
		if (this.props.operatorUpdatedHandler) {
			this.props.operatorUpdatedHandler();
		}
	}

	public animateFadeIn(): void {
		const element = this.container[0];
		element.style.opacity = '0';
		element.animate([
			{ opacity: 0, transform: 'scale(0.95)' },
			{ opacity: 1, transform: 'scale(1)' }
		], {
			duration: 150,
			iterations: 1
		}).onfinish = () => {
			element.style.opacity = '';
		};
	}

	public animateFadeOut(callback: () => void): void {
		const element = this.container[0];
		element.style.opacity = '1';
		element.animate([
			{ opacity: 1, transform: 'scale(1)' },
			{ opacity: 0.4, transform: 'scale(0.85)' },
			{ opacity: 0, transform: 'translateX(0)' },
			{ opacity: 0, transform: 'translateX(-100%)' }
		], {
			duration: 150,
			iterations: 1
		}).onfinish = () => {
			callback?.();
		};
	}
}
